import _toConsumableArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// https://swiper6.vercel.app/react
import 'swiper/swiper.min.css';
import 'swiper/css/effect-fade';
import 'swiper/css/effect-cube';
import classNames from 'classnames';
import { useMemo, useRef, useState } from 'react';
import { Autoplay, EffectCube, EffectFade } from 'swiper';
import { Swiper as SwiperComp, SwiperSlide } from 'swiper/react';
import { Image, VideoControl } from '../../common/components';
import { useMobile } from '../../common/hooks/use-mobile';
import { isClickable, transformPageMargins } from '../../common/utils';
import { Indicator } from './indicator';
import { Navigation } from './navigation';
import { useDesignContext } from '../../design-provider';
import { AspectRatioView } from '../../common/components/aspect-ratio-view';
import { VideoPathIframe } from '../../common/components/video-path-iframe';
export var Swiper = function Swiper(props) {
  var className = props.className,
    navigationClassName = props.navigationClassName,
    swiperCompClassName = props.swiperCompClassName,
    _props$imageAdList = props.imageAdList,
    imageAdList = _props$imageAdList === void 0 ? [] : _props$imageAdList,
    _props$interval = props.interval,
    interval = _props$interval === void 0 ? '5000' : _props$interval,
    _props$imageRadius = props.imageRadius,
    imageRadius = _props$imageRadius === void 0 ? 'rightAngle' : _props$imageRadius,
    _props$objectFit = props.objectFit,
    objectFit = _props$objectFit === void 0 ? 'fill' : _props$objectFit,
    _props$effect = props.effect,
    effect = _props$effect === void 0 ? 'ease' : _props$effect,
    _props$indicator = props.indicator,
    indicator = _props$indicator === void 0 ? '1' : _props$indicator,
    _props$arrow = props.arrow,
    arrow = _props$arrow === void 0 ? 'hover' : _props$arrow,
    _props$pagePadding = props.pagePadding,
    pagePadding = _props$pagePadding === void 0 ? 0 : _props$pagePadding;
  var wrapRef = useRef(null);
  var mobile = useMobile();
  var hasMultiItem = imageAdList.length > 1;
  var _useState = useState(0),
    realIndex = _useState[0],
    setRealIndex = _useState[1];
  var autoplay = useMemo(function () {
    if (interval !== 'close') {
      return {
        delay: +interval,
        disableOnInteraction: false
      };
    } else {
      return false;
    }
  }, [imageAdList, interval]);
  var aspectRatio = useMemo(function () {
    return {
      pc: Math.min.apply(Math, _toConsumableArray(imageAdList.map(function (item) {
        /**
         * 处理输入视频地址宽高比，视频的地址的宽高比使用的是图片的宽高比
         */
        if (item.fileType === 'videoPath') {
          return item.coverImage ? item.coverImage.web.aspectRatio : 1;
        }
        return item.fileValue.web.aspectRatio;
      }))),
      mobile: Math.min.apply(Math, _toConsumableArray(imageAdList.map(function (item) {
        /**
         * 处理输入视频地址宽高比，视频的地址的宽高比使用的是图片的宽高比
         */
        if (item.fileType === 'videoPath') {
          return item.coverImage ? item.coverImage.mobile.aspectRatio : 1;
        }
        return item.fileValue.mobile.aspectRatio;
      })))
    };
  }, [imageAdList]);
  return _jsxs("div", Object.assign({
    className: classNames('box-border overflow-hidden w-full', className),
    ref: wrapRef,
    style: {
      paddingLeft: transformPageMargins(pagePadding, mobile),
      paddingRight: transformPageMargins(pagePadding, mobile)
    }
  }, {
    children: [_jsx(AspectRatioView, Object.assign({
      className: 'w-full',
      aspectRatio: mobile ? aspectRatio.mobile : aspectRatio.pc
    }, {
      children: _jsx(Navigation, Object.assign({
        className: navigationClassName,
        arrow: arrow,
        navigation: hasMultiItem
      }, {
        children: _jsx(SwiperComp, Object.assign({
          className: swiperCompClassName,
          observer: true,
          observeParents: true,
          observeSlideChildren: true,
          resizeObserver: true,
          autoplay: autoplay,
          loop: hasMultiItem,
          allowTouchMove: hasMultiItem,
          speed: 1000,
          effect: effect,
          cubeEffect: {
            shadow: false,
            slideShadows: false
          },
          fadeEffect: {
            crossFade: true
          },
          modules: [Autoplay, EffectCube, EffectFade],
          onRealIndexChange: function onRealIndexChange(swiper) {
            return setRealIndex(swiper.realIndex);
          }
        }, {
          children: imageAdList.map(function (imageAd) {
            return _jsx(SwiperSlide, Object.assign({
              className: 'flex h-auto'
            }, {
              children: _jsx(RenderItem, {
                imageAd: imageAd,
                objectFit: objectFit,
                imageRadius: imageRadius,
                aspectRatio: aspectRatio
              })
            }), imageAd.id);
          })
        }), [effect, interval].join(':'))
      }))
    })), hasMultiItem && _jsx(Indicator, {
      indicator: indicator,
      total: imageAdList.length,
      current: realIndex
    })]
  }));
};
export var RenderItem = function RenderItem(_ref) {
  var imageAd = _ref.imageAd,
    objectFit = _ref.objectFit,
    imageRadius = _ref.imageRadius,
    aspectRatio = _ref.aspectRatio;
  var _useDesignContext = useDesignContext(),
    designOnLink = _useDesignContext.designOnLink;
  if (imageAd.fileType === 'image') {
    return _jsx("a", Object.assign({
      href: designOnLink(imageAd.target),
      className: classNames('block w-full h-full', {
        'cursor-pointer': isClickable(imageAd.target)
      })
    }, {
      children: _jsx(Image, {
        className: classNames('w-full h-full', {
          'cursor-pointer': isClickable(imageAd.target)
        }),
        style: {
          objectFit: objectFit,
          borderRadius: imageRadius === 'rightAngle' ? undefined : '20px'
        },
        src: imageAd.fileValue
      })
    }));
  }
  if (imageAd.fileType === 'video') {
    return _jsx(VideoControl, {
      className: 'w-full h-full flex items-center justify-center',
      imageStyle: {
        objectFit: objectFit,
        borderRadius: imageRadius === 'rightAngle' ? undefined : '20px'
      },
      imageClassName: 'w-full h-full',
      videoClassName: 'w-full h-full',
      videoStyle: {
        borderRadius: imageRadius === 'rightAngle' ? undefined : '20px'
      },
      coverImage: imageAd.coverImage,
      fileValue: imageAd.fileValue,
      playBtnClassName: classNames('w-16 h-16')
    });
  }
  if (imageAd.fileType === 'videoPath') {
    return _jsx(VideoPathIframe, {
      aspectRatio: aspectRatio,
      filePathValue: imageAd.filePathValue,
      coverImage: imageAd.coverImage,
      className: 'w-full h-full flex items-center justify-center',
      imageStyle: {
        objectFit: objectFit,
        borderRadius: imageRadius === 'rightAngle' ? undefined : '20px'
      },
      imageClassName: 'w-full h-full',
      playBtnClassName: classNames('w-16 h-16')
    });
  }
  return null;
};